import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Features } from '../../../config/features';
import { CacheReloadRequest, CacheReloadResponse } from '../../../interface/cache';
import useAuth from '../../../resources/auth/auth-hook';
import useToasts from '../../../resources/toasts/toasts-hook';
import cacheService from '../../../service/cache.service';
import './index.css';
import DropdownBtn, { Item } from '../../../components/ui/dropdown-btn';
import { WithFeaturesProxy } from '../../../helpers/with-features-proxy';

interface ExposedProps {
  getCacheData: (cache: string) => void;
  addToCacheData: (cache: CacheReloadResponse) => void;
  onSubmit: (request: CacheReloadRequest) => void;
  children?: React.ReactNode;
}

interface Props extends ExposedProps {}

const CacheReloaderFormComponent: React.FC<Props> = props => {
  const { getCacheData, onSubmit, children } = props;
  const auth = useAuth();
  const { push: pushToast } = useToasts();
  const [caches, setCaches] = useState<Item[]>([]);
  const [cache, setCache] = useState<string>('');
  const [source] = useState<string>(`${auth.user?.id}`);
  const [description, setDescription] = useState<string>('');
  const isValid = useMemo(() => {
    if (cache) {
      return !!(cache && description);
    }

    return !!(cache && description);
  }, [cache, description]);

  const updateCaches = useCallback(() => {
    cacheService
      .getCacheList()
      .then(response => {
        const responseItem: Item[] = response.map(cache => ({
          label: cache,
          id: cache,
        }));

        setCaches(responseItem);
      })
      .catch(err => {
        setCache('');
        setCaches([]);

        console.error(err);
        pushToast(`Could not fetch caches`, 'error');
      });
  }, [pushToast]);

  useEffect(() => {
    updateCaches();
  }, [pushToast, updateCaches]);

  const selectCache = useCallback(
    (item: Item) => {
      if (item) {
        const cache: string = item.label;
        setCache(cache);
        getCacheData(cache);
        setDescription('');
      }
    },
    [getCacheData],
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (cache && description && source) {
        const request: CacheReloadRequest = {
          cacheName: cache,
          description: description,
          changeSource: source,
        };
        setDescription('');
        onSubmit(request);
      }
    },
    [cache, description, source, onSubmit],
  );

  return (
    <Form className="cache-reloader-form" onSubmit={handleSubmit}>
      <Row>
        <Col>
          <Row className="mb-3">
            <Form.Group>
              <Form.Label htmlFor="source-ctrl">Source</Form.Label>
              <Form.Control id="source-ctrl" type="text" value={source} readOnly />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <DropdownBtn
              id="dropdown-cache-reloader-cache"
              required={true}
              label="Name"
              placeholder="Select Cache"
              disabled={caches.length === 0}
              items={caches}
              onClick={selectCache as unknown as (item?: Item | null) => void}
            />
          </Row>
        </Col>
        <Col>
          <Row className="mb-3">
            <Form.Group>
              <Form.Label htmlFor="description-ctrl" className="required">
                Description
              </Form.Label>
              <Form.Control
                as="textarea"
                id="description-ctrl"
                required
                value={description}
                rows={5}
                disabled={!cache}
                placeholder="Description"
                onChange={e => setDescription(e.target.value)}
              />
            </Form.Group>
          </Row>
        </Col>
      </Row>
      {children}
      <Row className="submit">
        <Col className="d-flex justify-content-center">
          <Button type="submit" disabled={!isValid}>
            RELOAD CACHE
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default WithFeaturesProxy<ExposedProps>(Features.CacheReloaderScreen)((props, _) => {
  return <CacheReloaderFormComponent {...props} />;
});
